import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useMediaQuery } from '@nebula/hooks';

import { products } from '../../../constants';
import isFlexUser from '../../../helpers/isFlexUser';
import { isPaidAnnualIdentityToMonthlyCreditExpert } from '../../../helpers/flex-helpers/paidAnnualIdentityToCreditExpert';
import { FamilyName } from '../constants.ts';
import useBillingChangeInfo from '../hooks/useBillingChangeInfo';
import { PaymentContext } from '../../PaymentOrchestrator/PaymentContextProvider';
import { StyledBodyLarge400, StyledBodyMedium400 } from './index.styles';

dayjs.extend(advancedFormat);

const SubHeading = ({ isTrial, upgradeOffer }) => {
  const theme = useContext(ThemeContext);
  const { isFlexFlow, resolvedCurrentSubscription } = useContext(PaymentContext);
  const { isDeferredUpsell, nextBillingDate, isProRataMovement } = useBillingChangeInfo({ upgradeOffer, isTrial });
  const reduxCurrentSubscription = useSelector(state => state.subscriptions.current);
  const currentSubscription = isFlexFlow ? resolvedCurrentSubscription : reduxCurrentSubscription;
  const isFlexSplitEnabled = useSelector(state => isFlexUser(state.split));
  const hasCurrentSubscriptionPlanCode = Boolean(currentSubscription?.planCode);
  const isFlexJourney = isFlexFlow || isFlexSplitEnabled || hasCurrentSubscriptionPlanCode;
  const isMovingToIdentity = upgradeOffer?.family === products.identity;
  const isCreditExpert = currentSubscription?.family === products.credit_expert;
  const isAnnualIdentityRefund = isPaidAnnualIdentityToMonthlyCreditExpert({
    currentSubscription,
    upgradeOffer,
    isFlexFlow,
  });
  const subscriptionEnd = nextBillingDate.format('D MMMM');
  const isDesktop = useMediaQuery(theme.media.smallDesktopMin);
  const SubHeadingText = isDesktop ? StyledBodyLarge400 : StyledBodyMedium400;

  return (
    <React.Fragment>
      {isTrial && isMovingToIdentity && !isCreditExpert && isFlexJourney && (
        <SubHeadingText>
          Identity Plus helps protect you from fraud by monitoring your details and alerting you to suspicious activity.
        </SubHeadingText>
      )}
      {isTrial && isMovingToIdentity && !isCreditExpert && !isFlexJourney && !isFlexFlow && (
        <SubHeadingText>'With complimentary access to CreditExpert features.'</SubHeadingText>
      )}
      {isDeferredUpsell && (
        <SubHeadingText>
          You’ll keep {FamilyName[currentSubscription.family]} until {subscriptionEnd}
        </SubHeadingText>
      )}
      {isAnnualIdentityRefund && (
        <SubHeadingText>
          Your CreditExpert subscription starts today. We&apos;ll cancel your annual Identity Plus subscription and
          refund you within 5 working days.
        </SubHeadingText>
      )}
      {isProRataMovement && !isAnnualIdentityRefund && (
        <React.Fragment>
          <SubHeadingText>Get instant access to all the extra benefits.</SubHeadingText>
          <SubHeadingText>
            To switch today, you'll need to pay the difference to cover the rest of this billing period.
          </SubHeadingText>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

SubHeading.propTypes = {
  upgradeOffer: PropTypes.shape({
    family: PropTypes.string.isRequired,
  }).isRequired,
  isTrial: PropTypes.bool.isRequired,
};

export default SubHeading;
